import { useState, useEffect } from "react";
import { BarChart3, TrendingUp, CheckCircle, XCircle, Clock, Activity, RefreshCw, Calendar } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import API from "@/lib/api";
import { toast } from "sonner";

const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, color: "#0F172A", fontSize: 12 };

const LAST_7 = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Today"];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d");

  useEffect(() => { load(); }, [range]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/sms/stats");
      setStats(data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };

  const deliveryData = stats ? [
    { name: "Delivered", value: stats.delivered || 0, fill: "#10B981" },
    { name: "Failed", value: stats.failed || 0, fill: "#EF4444" },
    { name: "Pending", value: stats.pending || 0, fill: "#F59E0B" },
    { name: "Submitted", value: stats.submitted || 0, fill: "#3B82F6" },
  ] : [];

  const weekData = LAST_7.map((d, i) => ({
    name: d,
    sent: i === 6 ? (stats?.today || 0) : 0,
    delivered: i === 6 ? Math.floor((stats?.today || 0) * ((stats?.delivery_rate || 90) / 100)) : 0,
  }));

  const statCards = [
    { icon: Activity, label: "Total SMS Sent", value: stats?.total || 0, color: "bg-[#0c1e41]", sub: "All time" },
    { icon: CheckCircle, label: "Delivered", value: stats?.delivered || 0, color: "bg-emerald-600", sub: `${stats?.delivery_rate || 0}% delivery rate` },
    { icon: XCircle, label: "Failed", value: stats?.failed || 0, color: "bg-red-500", sub: "Could not be delivered" },
    { icon: TrendingUp, label: "Today", value: stats?.today || 0, color: "bg-purple-600", sub: "Sent today" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">SMS delivery insights and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {["7d", "30d", "all"].map((r) => (
              <button key={r} onClick={() => setRange(r)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${range === r ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
                {r}
              </button>
            ))}
          </div>
          <button onClick={load} className="p-2 text-slate-500 hover:text-[#0c1e41] bg-white border border-slate-200 rounded-lg transition-colors shadow-sm">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
            <div className={`w-9 h-9 rounded-xl ${s.color} flex items-center justify-center mb-3 shadow-sm`}>
              <s.icon size={17} className="text-white" />
            </div>
            {loading ? (
              <div className="h-8 bg-slate-100 rounded animate-pulse mb-1" />
            ) : (
              <div className="text-2xl font-bold font-mono text-slate-900 mb-1">{(s.value || 0).toLocaleString()}</div>
            )}
            <div className="text-xs text-slate-500">{s.label}</div>
            {s.sub && <div className="text-xs text-slate-400 mt-0.5">{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Delivery Rate Highlight */}
      <div className="bg-gradient-to-r from-[#0c1e41] to-[#060e1c] rounded-2xl p-6 text-white shadow-xl shadow-blue-600/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-[#adc0e0] mb-1">Overall Delivery Rate</div>
            <div className="text-5xl font-extrabold font-mono">{stats?.delivery_rate || 0}%</div>
            <div className="text-sm text-[#adc0e0] mt-1">
              {stats?.delivered?.toLocaleString() || 0} of {stats?.total?.toLocaleString() || 0} messages delivered
            </div>
          </div>
          <div className="hidden sm:block">
            <div className="w-24 h-24 rounded-full border-4 border-[#0c1e41]/20 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <TrendingUp size={28} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly trend */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Weekly Activity</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weekData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="sent" fill="#BFDBFE" radius={[4, 4, 0, 0]} name="Sent" />
              <Bar dataKey="delivered" fill="#2563EB" radius={[4, 4, 0, 0]} name="Delivered" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Status breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Status Breakdown</h3>
          {loading || !stats?.total ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No data yet. Send your first SMS to see analytics.</div>
          ) : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={deliveryData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {deliveryData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 flex-1">
                {deliveryData.map((d) => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
                    <span className="text-xs text-slate-600 flex-1">{d.name}</span>
                    <span className="text-xs font-mono font-bold text-slate-900">{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Month summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar size={15} className="text-[#0c1e41]" /> Monthly Summary
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { label: "This Month", value: stats?.this_month || 0, color: "text-[#0c1e41]", bg: "bg-[#eef1f8] border-[#c0cfe8]" },
            { label: "This Week", value: stats?.this_week || 0, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
            { label: "Today", value: stats?.today || 0, color: "text-purple-700", bg: "bg-purple-50 border-purple-200" },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} border rounded-xl p-4 text-center`}>
              <div className={`text-2xl font-extrabold font-mono ${s.color}`}>{(s.value || 0).toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
